import React from 'react';
import { Dropdown, Text } from '@rsos/base-components';
import {
  DropdownBody,
  DropdownDialog,
  DropdownList,
  DropdownListItem,
} from '@rsos/base-components/Dropdown/DropdownComponents';
import type { KeywordSet } from '@rsos/reduxDataStore';
import {
  keywordSpeakerDisplayText,
  useKeywordSetActions,
} from '@rsos/reduxDataStore';
import {
  trackChangedKeywordTopicEvent,
  trackDeletedKeywordTopicEvent,
} from '../../../tracking/KeywordAdminEvents';
import { ConfirmationModal } from '../../ConfirmationModal/ConfirmationModal';
import { KeywordModal } from '../../KeywordModal/KeywordModal';
import { Tier } from '../../Tier/Tier';
import {
  StyledActionCell,
  StyledCell,
  StyledEditIcon,
  StyledMenuIcon,
  StyledNameCell,
  StyledRow,
  StyledWordsCell,
} from './KeywordRow.styles';

export type KeywordRowProps = {
  keywordSet: KeywordSet;
};

export const KeywordRow = ({ keywordSet }: KeywordRowProps) => {
  const { update } = useKeywordSetActions(keywordSet);
  const instrumentedUpdate = (keywordSet: KeywordSet) => {
    trackChangedKeywordTopicEvent(keywordSet);
    update(keywordSet);
  };
  return (
    <StyledRow>
      <StyledNameCell>
        <Text fontWeight="bold">{keywordSet.name}</Text>
      </StyledNameCell>
      <StyledCell>
        <Tier tierId={keywordSet.tier_id} />
      </StyledCell>
      <StyledWordsCell>{keywordSet.keywords.join(', ')}</StyledWordsCell>
      <StyledCell>{keywordSpeakerDisplayText(keywordSet.speaker)}</StyledCell>
      <StyledActionCell>
        <KeywordModal
          id={keywordSet.id}
          keywordSet={keywordSet}
          title="Edit Keyword Topic"
          save={instrumentedUpdate}
        >
          {({ openModal }) => <StyledEditIcon onClick={openModal} />}
        </KeywordModal>
      </StyledActionCell>
      <StyledActionCell padding="0px 16px">
        <ActionMenu keywordSet={keywordSet} />
      </StyledActionCell>
    </StyledRow>
  );
};

function ActionMenu({ keywordSet }: { keywordSet: KeywordSet }) {
  const { destroy, duplicate } = useKeywordSetActions(keywordSet);
  const instrumentedDestroy = () => {
    trackDeletedKeywordTopicEvent(keywordSet);
    destroy();
  };
  /**
   * This confirmation modal has to live outside the dropdown, because the
   * dropdown destroys itself on any click outside itself; if a modal lives
   * inside, it's impossible to click "yes" before it self-destructs.
   */
  return (
    <ConfirmationModal
      action={instrumentedDestroy}
      title="Delete Keyword Topic?"
      doItText="Delete"
      bodyText="This action cannot be undone."
    >
      {({ confirm: confirmDelete }) => (
        <Dropdown triggerElement={TriggerElement} triggerOnClose>
          {closeDropdown => (
            <DropdownDialog zIndex={2}>
              <DropdownBody width={180}>
                <DropdownList>
                  <DropdownListItem onClick={duplicate}>
                    Duplicate
                  </DropdownListItem>
                  <DropdownListItem
                    onClick={() => {
                      closeDropdown();
                      confirmDelete();
                    }}
                  >
                    Delete
                  </DropdownListItem>
                </DropdownList>
              </DropdownBody>
            </DropdownDialog>
          )}
        </Dropdown>
      )}
    </ConfirmationModal>
  );
}

function TriggerElement({
  onClick,
}: {
  selected: boolean;
  onClick?: () => void;
}) {
  return <StyledMenuIcon onClick={onClick} />;
}
